(function () {
  'use strict';

  function getWrapperVideo(host) {
    var wrapper = host.closest('video-wrapper');
    return wrapper ? wrapper.querySelector('video') : null;
  }

  function setButtonVisibility(button, isVisible) {
    button.hidden = !isVisible;
    button.style.display = isVisible ? '' : 'none';
    button.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    button.tabIndex = isVisible ? 0 : -1;
  }

  function transferToggleFocus(hiddenButton, visibleButton) {
    if (document.activeElement === hiddenButton) {
      visibleButton.focus();
    }
  }

  function ensureVideoId(video, prefix) {
    if (!video.id) {
      video.id = prefix + '-' + Math.random().toString(36).slice(2, 10);
    }
    return video.id;
  }

  function pauseHighlightThumbsForReducedMotion() {
    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('video.video-highlight-thumb').forEach(function (video) {
      if (prefersReducedMotion) {
        video.removeAttribute('autoplay');
        video.pause();
        try {
          video.currentTime = 0;
        } catch (e) {}
      } else if (video.paused && video.hasAttribute('loop')) {
        video.setAttribute('autoplay', '');
        video.play().catch(function () {});
      }
    });
  }

  function hydrateLazyVideoSources(video) {
    if (!video) return;

    var changed = false;
    var videoDataSrc = video.getAttribute('data-src');
    if (videoDataSrc) {
      video.src = videoDataSrc;
      video.removeAttribute('data-src');
      changed = true;
    }

    video.querySelectorAll('source[data-src]').forEach(function (source) {
      var url = source.getAttribute('data-src');
      if (url) {
        source.src = url;
        source.removeAttribute('data-src');
        changed = true;
      }
    });

    if (changed) {
      video.load();
    }
  }

  function getFullscreenElement() {
    return document.fullscreenElement || document.webkitFullscreenElement || null;
  }

  function supportsFullscreen(wrapper, video) {
    return Boolean(
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      (wrapper && wrapper.requestFullscreen) ||
      (wrapper && wrapper.webkitRequestFullscreen) ||
      (video && typeof video.webkitEnterFullscreen === 'function')
    );
  }

  function isFullscreenFor(wrapper, video) {
    var el = getFullscreenElement();
    if (el) {
      return el === wrapper || el === video || Boolean(wrapper && wrapper.contains(el));
    }
    return Boolean(video && video.webkitDisplayingFullscreen);
  }

  function enterFullscreen(wrapper, video) {
    var target = wrapper || video;
    try {
      if (target && target.requestFullscreen) {
        return target.requestFullscreen();
      }
      if (target && target.webkitRequestFullscreen) {
        target.webkitRequestFullscreen();
        return Promise.resolve();
      }
    } catch (e) {}

    if (video && typeof video.webkitEnterFullscreen === 'function') {
      video.webkitEnterFullscreen();
    }
    return Promise.resolve();
  }

  function exitFullscreen(video, container) {
    var fsEl = getFullscreenElement();
    if (fsEl && (!container || container.contains(fsEl) || fsEl === container)) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(function () {});
        return;
      }
      if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
        return;
      }
    }
    if (video && typeof video.webkitExitFullscreen === 'function') {
      video.webkitExitFullscreen();
    }
  }

  function formatVideoTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
    var total = Math.floor(seconds);
    var hours = Math.floor(total / 3600);
    var minutes = Math.floor((total % 3600) / 60);
    var secs = total % 60;

    if (hours > 0) {
      return hours + ':' + String(minutes).padStart(2, '0') + ':' + String(secs).padStart(2, '0');
    }

    return minutes + ':' + String(secs).padStart(2, '0');
  }

  class VideoToggle extends HTMLElement {
    constructor() {
      super();
      this._video = null;
      this._pauseButton = null;
      this._playButton = null;
      this._onPauseClick = this._onPauseClick.bind(this);
      this._onPlayClick = this._onPlayClick.bind(this);
      this._sync = this._sync.bind(this);
    }

    connectedCallback() {
      this._video = getWrapperVideo(this);
      if (!this._video) return;

      var buttons = this.querySelectorAll('button');
      this._pauseButton = buttons[0] || null;
      this._playButton = buttons[1] || null;
      if (!this._pauseButton || !this._playButton) return;

      var id = ensureVideoId(this._video, 'video-toggle');
      this._pauseButton.setAttribute('aria-controls', id);
      this._playButton.setAttribute('aria-controls', id);

      this._pauseButton.addEventListener('click', this._onPauseClick);
      this._playButton.addEventListener('click', this._onPlayClick);
      this._video.addEventListener('play', this._sync);
      this._video.addEventListener('pause', this._sync);
      this._video.addEventListener('ended', this._sync);

      this._sync();
    }

    disconnectedCallback() {
      if (this._pauseButton) {
        this._pauseButton.removeEventListener('click', this._onPauseClick);
      }
      if (this._playButton) {
        this._playButton.removeEventListener('click', this._onPlayClick);
      }
      if (this._video) {
        this._video.removeEventListener('play', this._sync);
        this._video.removeEventListener('pause', this._sync);
        this._video.removeEventListener('ended', this._sync);
      }
    }

    _onPauseClick() {
      if (this._video) this._video.pause();
    }

    _onPlayClick() {
      if (this._video) this._video.play().catch(function () {});
    }

    _sync() {
      if (!this._video || !this._pauseButton || !this._playButton) return;

      var isPlaying = !this._video.paused && !this._video.ended;
      this.classList.toggle('playing', isPlaying);
      this._video.classList.toggle('playing', isPlaying);

      setButtonVisibility(this._pauseButton, isPlaying);
      setButtonVisibility(this._playButton, !isPlaying);

      if (!isPlaying) {
        transferToggleFocus(this._pauseButton, this._playButton);
      } else {
        transferToggleFocus(this._playButton, this._pauseButton);
      }
    }
  }

  class VideoMuteToggle extends HTMLElement {
    constructor() {
      super();
      this._video = null;
      this._muteButton = null;
      this._unmuteButton = null;
      this._onMuteClick = this._onMuteClick.bind(this);
      this._onUnmuteClick = this._onUnmuteClick.bind(this);
      this._sync = this._sync.bind(this);
    }

    connectedCallback() {
      this._video = getWrapperVideo(this);
      if (!this._video) return;

      var buttons = this.querySelectorAll('button');
      this._muteButton = buttons[0] || null;
      this._unmuteButton = buttons[1] || null;
      if (!this._muteButton || !this._unmuteButton) return;

      var id = ensureVideoId(this._video, 'video-mute-toggle');
      this._muteButton.setAttribute('aria-controls', id);
      this._unmuteButton.setAttribute('aria-controls', id);

      this._muteButton.addEventListener('click', this._onMuteClick);
      this._unmuteButton.addEventListener('click', this._onUnmuteClick);
      this._video.addEventListener('volumechange', this._sync);

      this._sync();
    }

    disconnectedCallback() {
      if (this._muteButton) {
        this._muteButton.removeEventListener('click', this._onMuteClick);
      }
      if (this._unmuteButton) {
        this._unmuteButton.removeEventListener('click', this._onUnmuteClick);
      }
      if (this._video) {
        this._video.removeEventListener('volumechange', this._sync);
      }
    }

    _onMuteClick() {
      if (!this._video) return;
      this._video.muted = true;
    }

    _onUnmuteClick() {
      if (!this._video) return;
      this._video.muted = false;
    }

    _sync() {
      if (!this._video || !this._muteButton || !this._unmuteButton) return;

      var isMuted = this._video.muted || this._video.volume === 0;
      this.classList.toggle('muted', isMuted);
      this._video.classList.toggle('muted', isMuted);

      setButtonVisibility(this._muteButton, !isMuted);
      setButtonVisibility(this._unmuteButton, isMuted);

      if (isMuted) {
        transferToggleFocus(this._muteButton, this._unmuteButton);
      } else {
        transferToggleFocus(this._unmuteButton, this._muteButton);
      }
    }
  }

  class VideoFullscreenToggle extends HTMLElement {
    constructor() {
      super();
      this._video = null;
      this._wrapper = null;
      this._expandButton = null;
      this._compressButton = null;
      this._onExpandClick = this._onExpandClick.bind(this);
      this._onCompressClick = this._onCompressClick.bind(this);
      this._sync = this._sync.bind(this);
    }

    connectedCallback() {
      this._wrapper = this.closest('video-wrapper');
      this._video = getWrapperVideo(this);
      if (!this._video) return;

      var buttons = this.querySelectorAll('button');
      this._expandButton = buttons[0] || null;
      this._compressButton = buttons[1] || null;
      if (!this._expandButton || !this._compressButton) return;

      if (!supportsFullscreen(this._wrapper, this._video)) {
        this.hidden = true;
        return;
      }

      var id = ensureVideoId(this._video, 'video-fullscreen-toggle');
      this._expandButton.setAttribute('aria-controls', id);
      this._compressButton.setAttribute('aria-controls', id);

      this._expandButton.addEventListener('click', this._onExpandClick);
      this._compressButton.addEventListener('click', this._onCompressClick);
      document.addEventListener('fullscreenchange', this._sync);
      document.addEventListener('webkitfullscreenchange', this._sync);
      this._video.addEventListener('webkitbeginfullscreen', this._sync);
      this._video.addEventListener('webkitendfullscreen', this._sync);

      this._sync();
    }

    disconnectedCallback() {
      if (this._expandButton) {
        this._expandButton.removeEventListener('click', this._onExpandClick);
      }
      if (this._compressButton) {
        this._compressButton.removeEventListener('click', this._onCompressClick);
      }
      document.removeEventListener('fullscreenchange', this._sync);
      document.removeEventListener('webkitfullscreenchange', this._sync);
      if (this._video) {
        this._video.removeEventListener('webkitbeginfullscreen', this._sync);
        this._video.removeEventListener('webkitendfullscreen', this._sync);
      }
    }

    _onExpandClick() {
      enterFullscreen(this._wrapper, this._video);
    }

    _onCompressClick() {
      exitFullscreen(this._video, this._wrapper);
    }

    _sync() {
      if (!this._expandButton || !this._compressButton) return;

      var isFullscreen = isFullscreenFor(this._wrapper, this._video);
      var wrapperIsFullscreen = Boolean(
        this._wrapper &&
        (getFullscreenElement() === this._wrapper || this._wrapper.contains(getFullscreenElement()))
      );
      this.classList.toggle('is-fullscreen', isFullscreen);
      if (this._wrapper) this._wrapper.classList.toggle('is-fullscreen', wrapperIsFullscreen);

      setButtonVisibility(this._expandButton, !isFullscreen);
      setButtonVisibility(this._compressButton, isFullscreen);

      if (isFullscreen) {
        transferToggleFocus(this._expandButton, this._compressButton);
      } else {
        transferToggleFocus(this._compressButton, this._expandButton);
      }
    }
  }

  class VideoProgress extends HTMLElement {
    constructor() {
      super();
      this._video = null;
      this._input = null;
      this._currentEl = null;
      this._durationEl = null;
      this._scrubbing = false;
      this._onInput = this._onInput.bind(this);
      this._onChange = this._onChange.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._sync = this._sync.bind(this);
      this._syncDuration = this._syncDuration.bind(this);
    }

    connectedCallback() {
      this._video = getWrapperVideo(this);
      this._input = this.querySelector('input[type="range"]');
      if (!this._video || !this._input) return;

      this.classList.add('video-progress');
      this._ensureChrome();

      var id = ensureVideoId(this._video, 'video-progress');
      this._input.setAttribute('aria-controls', id);
      if (!this._input.getAttribute('aria-label')) {
        this._input.setAttribute('aria-label', 'Seek video');
      }

      this._input.addEventListener('input', this._onInput);
      this._input.addEventListener('change', this._onChange);
      this._input.addEventListener('pointerdown', this._onPointerDown);
      this._input.addEventListener('pointerup', this._onPointerUp);
      this._video.addEventListener('timeupdate', this._sync);
      this._video.addEventListener('loadedmetadata', this._syncDuration);
      this._video.addEventListener('durationchange', this._syncDuration);

      this._syncDuration();
      this._sync();
    }

    disconnectedCallback() {
      if (this._input) {
        this._input.removeEventListener('input', this._onInput);
        this._input.removeEventListener('change', this._onChange);
        this._input.removeEventListener('pointerdown', this._onPointerDown);
        this._input.removeEventListener('pointerup', this._onPointerUp);
      }
      if (this._video) {
        this._video.removeEventListener('timeupdate', this._sync);
        this._video.removeEventListener('loadedmetadata', this._syncDuration);
        this._video.removeEventListener('durationchange', this._syncDuration);
      }
    }

    _ensureChrome() {
      var slider = this.querySelector('.video-progress__slider');
      if (!slider) {
        slider = document.createElement('div');
        slider.className = 'video-progress__slider';
        this.insertBefore(slider, this._input);
        slider.appendChild(this._input);
      }

      if (!slider.querySelector('.video-progress__track')) {
        var track = document.createElement('div');
        track.className = 'video-progress__track';
        track.setAttribute('aria-hidden', 'true');

        var fill = document.createElement('div');
        fill.className = 'video-progress__fill';
        track.appendChild(fill);
        slider.insertBefore(track, this._input);
      }

      this._currentEl =
        this.querySelector('[data-video-progress-current]') ||
        this.querySelector('.video-progress__current');
      if (!this._currentEl) {
        this._currentEl = document.createElement('span');
        this._currentEl.className = 'video-progress__current';
        this._currentEl.setAttribute('data-video-progress-current', '');
        this._currentEl.setAttribute('aria-hidden', 'true');
        this.insertBefore(this._currentEl, slider);
      }

      this._durationEl =
        this.querySelector('[data-video-progress-duration]') ||
        this.querySelector('.video-progress__duration');
      if (!this._durationEl) {
        this._durationEl = document.createElement('span');
        this._durationEl.className = 'video-progress__duration';
        this._durationEl.setAttribute('data-video-progress-duration', '');
        this._durationEl.setAttribute('aria-hidden', 'true');
        this.appendChild(this._durationEl);
      }
    }

    _onPointerDown() {
      this._scrubbing = true;
    }

    _onPointerUp() {
      this._scrubbing = false;
      this._seekFromInput();
    }

    _onInput() {
      this._scrubbing = true;
      this._seekFromInput();
    }

    _onChange() {
      this._scrubbing = false;
      this._seekFromInput();
    }

    _seekFromInput() {
      if (!this._video || !this._input) return;
      var duration = this._video.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;

      var max = Number(this._input.max) || 1000;
      var ratio = Number(this._input.value) / max;
      this._video.currentTime = ratio * duration;
      this._setProgressVar(ratio);
      this._setTimes(ratio * duration, duration);
    }

    _syncDuration() {
      if (!this._input) return;
      if (!this._input.max) {
        this._input.max = '1000';
      }
      if (!this._input.min) {
        this._input.min = '0';
      }
      this._sync();
    }

    _setProgressVar(ratio) {
      var pct = Math.min(100, Math.max(0, ratio * 100)) + '%';
      this.style.setProperty('--video-progress', pct);
    }

    _setTimes(current, duration) {
      var currentLabel = formatVideoTime(current);
      var durationLabel = formatVideoTime(duration);

      if (this._currentEl) {
        this._currentEl.textContent = currentLabel;
      }
      if (this._durationEl) {
        this._durationEl.textContent = durationLabel;
      }
      if (this._input) {
        this._input.setAttribute('aria-valuetext', currentLabel + ' of ' + durationLabel);
      }
    }

    _sync() {
      if (!this._video || !this._input || this._scrubbing) return;

      var duration = this._video.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        this._input.value = '0';
        this._setProgressVar(0);
        this._setTimes(0, 0);
        return;
      }

      var max = Number(this._input.max) || 1000;
      var current = this._video.currentTime;
      var ratio = current / duration;
      this._input.value = String(ratio * max);
      this._setProgressVar(ratio);
      this._setTimes(current, duration);
    }
  }

  class VideoHighlights extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
      this._onDialogBackdropClick = this._onDialogBackdropClick.bind(this);
      this._resetTimers = new WeakMap();
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this.querySelectorAll('dialog').forEach(function (dialog) {
        dialog.addEventListener('close', this._onDialogClose);
        dialog.addEventListener('click', this._onDialogBackdropClick);
      }, this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.querySelectorAll('dialog').forEach(function (dialog) {
        dialog.removeEventListener('close', this._onDialogClose);
        dialog.removeEventListener('click', this._onDialogBackdropClick);
      }, this);
    }

    _onClick(event) {
      var closeBtn = event.target.closest('[data-video-highlight-close]');
      if (closeBtn && this.contains(closeBtn)) {
        var closeDialog = closeBtn.closest('dialog');
        if (closeDialog && closeDialog.open) closeDialog.close();
        return;
      }

      var openBtn = event.target.closest('[data-video-highlight-open]');
      if (!openBtn || !this.contains(openBtn)) return;

      var id = openBtn.getAttribute('data-video-highlight-open');
      if (!id) return;
      var dialog = document.getElementById(id);
      if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;

      dialog.showModal();
      this._onDialogOpen(dialog);
    }

    _onDialogBackdropClick(event) {
      if (event.target === event.currentTarget && event.currentTarget.open) {
        event.currentTarget.close();
      }
    }

    _pauseAndMute(video, dialog) {
      if (!video) return;
      exitFullscreen(video, dialog);
      video.pause();
      video.muted = true;
    }

    _onDialogOpen(dialog) {
      dialog._vhClosed = false;
      var timer = this._resetTimers.get(dialog);
      if (timer) {
        clearTimeout(timer);
        this._resetTimers.delete(dialog);
      }

      var video = dialog.querySelector('video');
      if (!video) return;
      hydrateLazyVideoSources(video);
      video.muted = false;
      var playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.then(function () {
          if (dialog._vhClosed || !dialog.open) {
            video.pause();
            video.muted = true;
          }
        }).catch(function () {});
      }
    }

    _onDialogClose(event) {
      var dialog = event.target;
      dialog._vhClosed = true;
      var video = dialog.querySelector('video');
      if (!video) return;

      this._pauseAndMute(video, dialog);

      var existing = this._resetTimers.get(dialog);
      if (existing) clearTimeout(existing);

      var timer = setTimeout(function () {
        try {
          video.currentTime = 0;
        } catch (e) {}
      }, 300);
      this._resetTimers.set(dialog, timer);
    }
  }

  if (!window.customElements.get('video-toggle')) {
    customElements.define('video-toggle', VideoToggle);
  }
  if (!window.customElements.get('video-mute-toggle')) {
    customElements.define('video-mute-toggle', VideoMuteToggle);
  }
  if (!window.customElements.get('video-fullscreen-toggle')) {
    customElements.define('video-fullscreen-toggle', VideoFullscreenToggle);
  }
  if (!window.customElements.get('video-progress')) {
    customElements.define('video-progress', VideoProgress);
  }
  if (!window.customElements.get('video-highlights')) {
    customElements.define('video-highlights', VideoHighlights);
  }

  var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  pauseHighlightThumbsForReducedMotion();
  if (typeof reducedMotionQuery.addEventListener === 'function') {
    reducedMotionQuery.addEventListener('change', pauseHighlightThumbsForReducedMotion);
  } else if (typeof reducedMotionQuery.addListener === 'function') {
    reducedMotionQuery.addListener(pauseHighlightThumbsForReducedMotion);
  }
})();
